import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Link } from 'components/Toolkit/Button/Link';
import { Title } from 'components/Toolkit/InfoTitle/Title/Title';
import { SubtitleItem } from 'components/Toolkit/InfoTitle/SubtitleItem/SubtitleItem';

export type Variants = 'DEFAULT' | 'SMALL' | 'XS';

export interface InfoTitleProps {
  title: string | ReactNode;
  description?: string | ReactNode;
  illustration?: string;
  icon?: React.ReactNode;
  listItems?: (string | undefined | ReactNode)[];
  className?: string;
  shouldTruncate?: boolean;
  withH1Tag?: boolean;
  minHeight?: boolean;
  /* This allows truncation to occur at a specified
  width on large screens, replacing the max-width
  value associated with shouldTruncate */
  largeTruncationWidth?: number;
  /* this replaces the default value associated with
  shouldTruncate on all screens */
  smallTruncationWidth?: number;
  variant?: Variants;
  flip?: boolean;
  linkHref?: string;
  linkText?: string;
  disabled?: boolean;
}

export const InfoTitleContainer = styled.div<{
  variant: Variants;
  minHeight: boolean;
}>`
  ${({ variant, minHeight }) =>
    minHeight &&
    css`
      min-height: ${variant === 'SMALL'
        ? '40px'
        : variant === 'XS'
        ? '16px'
        : '48px'};
    `}
  display: flex;
  align-items: center;
  width: 100%;
`;

const ImageContainer = styled.div<{ variant: Variants }>`
  flex: 0 0 auto;
  margin-right: ${({ theme, variant }) =>
    variant === 'DEFAULT' ? theme.spacing.M16 : theme.spacing.S8};
  width: ${({ variant }) => (variant === 'XS' ? '20px' : '40px')};
  ${({ variant }) => variant === 'XS' && 'height: 20px'};

  img {
    width: 100%;
    height: auto;
  }
`;

export const IconContainer = styled.div<{ variant: Variants }>`
  margin-right: ${({ theme, variant }) =>
    variant === 'DEFAULT' ? theme.spacing.M16 : theme.spacing.S8};
`;

const Description = styled.p<{ variant: Variants; disabled?: boolean }>`
  display: block;
  margin: 0;
  ${({ theme, variant }) =>
    variant === 'SMALL' || variant === 'XS'
      ? theme.fontSize.B12
      : theme.fontSize.M14};
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.GREY : theme.colors.GREY_DARK};
`;

const SubtitleList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

export const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
`;

const SLink = styled(Link)`
  ${({ theme }) => theme.fontSize.B12};
`;

function InfoTitle({
  title,
  description,
  illustration,
  icon,
  listItems,
  className,
  shouldTruncate = false,
  largeTruncationWidth,
  smallTruncationWidth,
  withH1Tag = false,
  variant = 'DEFAULT',
  minHeight = true,
  flip = false,
  linkHref,
  linkText,
  disabled,
}: InfoTitleProps) {
  return (
    <InfoTitleContainer
      className={className}
      variant={variant}
      minHeight={minHeight}
    >
      {illustration && (
        <ImageContainer variant={variant}>
          <img src={illustration} alt={`${title} image`} />
        </ImageContainer>
      )}
      {icon && <IconContainer variant={variant}>{icon}</IconContainer>}
      <Content>
        <Title
          as={withH1Tag ? 'h1' : 'p'}
          shouldTruncate={shouldTruncate}
          variant={variant}
          largeTruncationWidth={largeTruncationWidth}
          smallTruncationWidth={smallTruncationWidth}
          flip={flip}
          disabled={disabled}
        >
          {title}
        </Title>
        {!!description && (
          <Description variant={variant} disabled={disabled}>
            {description}
          </Description>
        )}
        {listItems && (
          <SubtitleList>
            {listItems.map(
              (item, index) =>
                item && (
                  <SubtitleItem key={index} variant={variant}>
                    {item}
                  </SubtitleItem>
                ),
            )}
          </SubtitleList>
        )}

        {linkHref && linkText && (
          <SLink ofType="SECONDARY" href={linkHref}>
            {linkText}
          </SLink>
        )}
      </Content>
    </InfoTitleContainer>
  );
}

export { InfoTitle };
